"use client"

import styled from "@emotion/styled"
import { Box, Divider, Typography } from "@mui/material"
import Image from "next/image"
import { colors, mediaQueries, sizes } from "ui/theme/theme"

interface IconData {
  src: string
  alt?: string
  width?: string | number
  height?: string | number
}

interface BubbleData {
  title?: string
  icon?: IconData
  smallRightText?: string
  bigRightText?: string
  smallLeftText?: string
  bigLeftText?: string
}

interface BubbleInfoCardProps {
  data: BubbleData
  className?: string
}

// Bubble component styles
const StyledBubble = styled(Box)`
  display: flex;
  flex-direction: column;
  padding: ${sizes.baseTriple};
  border: 2px solid ${colors.red};
  border-radius: ${sizes.borderRadiusLarge};
  background-color: ${colors.white};
  box-shadow: -5px 10px 30px 0px rgba(2, 1, 64, 0.08);
`

const StyledBubbleTitle = styled(Box)`
  display: flex;
  align-items: center;
  margin-bottom: ${sizes.baseDouble};

  h3 {
    color: ${colors.black};
    font-weight: 700;
  }
`

const StyledInfoSection = styled(Box)`
  display: flex;
  justify-content: space-between;
`

const StyledInfoBlock = styled(Box)`
  margin-bottom: ${sizes.baseDouble};
  display: flex;
  flex-direction: column;
  text-align: start;

  ${mediaQueries.tablet} {
    width: 48%;
    margin-bottom: 0;
  }
`

const StyledSmallText = styled(Typography)`
  color: ${colors.black};
  font-weight: 400;
  font-size: 14px;
`

const StyledBigText = styled(Typography)`
  color: ${colors.black};
  font-weight: 700;
  text-align: start;
  ${mediaQueries.tabletDown} {
    font-size: 16px;
  }
  ${mediaQueries.tablet} {
    font-size: 18px;
  }
`

const StyledVerticalDivider = styled(Divider)`
  display: block;
  height: auto;
  margin: 0 ${sizes.baseDouble};
  ${mediaQueries.desktopM} {
    margin-right: ${sizes.baseQuadric};
  }
`

const StyledIconWrapper = styled(Box)`
  position: relative;
  width: 32px;
  height: 32px;
  min-width: 32px; /* Ensures the icon maintains minimum width */
`

const StyledRightSection = styled(Box)`
  display: flex;
  align-items: flex-start;

  ${mediaQueries.tablet} {
    align-items: center;
  }
`
const StyledTexts = styled(Box)`
  padding-left: ${sizes.baseDouble};
`

/**
 * BubbleInfoCard - A reusable component that displays information in a bubble format
 * with a title, left and right sections separated by a divider.
 */
const BubbleInfoCard: React.FC<BubbleInfoCardProps> = ({ data, className }) => {
  // Extract data from the data object
  const {
    title,
    icon,
    smallRightText,
    bigRightText,
    smallLeftText,
    bigLeftText,
  } = data

  return (
    <StyledBubble className={className}>
      {title && (
        <StyledBubbleTitle>
          <Typography variant="h5" component="h3">
            {title}
          </Typography>
        </StyledBubbleTitle>
      )}

      <StyledInfoSection>
        <StyledInfoBlock>
          <StyledRightSection>
            {icon && (
              <StyledIconWrapper>
                <Image src={icon.src} alt={icon.alt || "Bubble icon"} fill />
              </StyledIconWrapper>
            )}
            <StyledTexts>
              {smallRightText && (
                <StyledSmallText variant="body1">
                  {smallRightText}
                </StyledSmallText>
              )}
              {bigRightText && <StyledBigText>{bigRightText}</StyledBigText>}
            </StyledTexts>
          </StyledRightSection>
        </StyledInfoBlock>

        <StyledVerticalDivider orientation="vertical" flexItem />

        <StyledInfoBlock>
          {smallLeftText && (
            <StyledSmallText variant="body1">{smallLeftText}</StyledSmallText>
          )}
          {bigLeftText && <StyledBigText>{bigLeftText}</StyledBigText>}
        </StyledInfoBlock>
      </StyledInfoSection>
    </StyledBubble>
  )
}

export default BubbleInfoCard
